<script>
    import {_, number} from 'svelte-i18n';
    import Countdown from '../Countdown.svelte';
    import {storePacks} from '../../stores.js';
    import {claimAll, claimDailyBonus} from './api.js';
    import {now} from './now.js';
    import {premiumPriceLabel, earnRateAmount, findSeasonPassPack, shouldReportClaimError} from './utils.js';
    import banner from '../../assets/web_battle_pass/banner.png?format=webp;jpg';

    let { event, seasonOver, claimWindowOver, onclaimed, onunlockpremium } = $props();

    const DAY_MS = 24 * 60 * 60 * 1000;

    let claimingAll = $state(false);
    let claimingDaily = $state(false);

    // Premium price comes from the actual pack's localized price (what's charged).
    let seasonPassPack = $derived(findSeasonPassPack($storePacks, event));

    // "RON 5.00 = 50 points" — amount is the $1 accrual rate converted into the
    // pack's PaymentHub currency (what the player is actually charged); wording
    // localised, with the value interpolated.
    let earnRateLabel = $derived($_('WebBattlePassEarnRate', {
        values: { amount: earnRateAmount(seasonPassPack), points: event.pointsPerCurrencyUnit },
        default: '{amount} = {points} points'
    }));

    let nextDailyAt = $derived(
        event.dailyBonus.lastClaimedAt
            ? new Date(new Date(event.dailyBonus.lastClaimedAt).getTime() + DAY_MS)
            : null
    );
    let dailyOnCooldown = $derived(nextDailyAt !== null && nextDailyAt.getTime() > $now);

    let hasAnyClaimable = $derived(
        !claimWindowOver &&
        Object.entries(event.prizes).some(([key, prize]) => {
            if (event.points < prize.pointsNeeded) return false;
            const claimable = event.claimablePrizes[key];
            return claimable?.claimed === 0 ||
                (event.hasEventPass && claimable?.seasonPassClaimed === 0);
        })
    );

    async function handleClaimAll() {
        if (claimingAll) return;
        claimingAll = true;
        // Optimistic: claimAll marks everything claimed locally and fires one
        // consolidated grant; we just surface the resulting items.
        const result = await claimAll();
        claimingAll = false;
        onclaimed?.(result.grantedItems);
    }

    async function handleDailyBonus() {
        if (claimingDaily || dailyOnCooldown) return;
        claimingDaily = true;
        const result = await claimDailyBonus();
        claimingDaily = false;
        // Transport failures and session expiry already reported themselves from api.js
        // (Rollbar #2519 / #2463 / #2535), so only a business rejection is news here.
        if (shouldReportClaimError(result.error)) {
            Rollbar.warning('Web battle pass daily bonus rejected: ' + result.error);
        }
        // claimDailyBonus applies the refreshed event to the store itself.
    }
</script>

<div class="bp-header">
    <div class="hero">
        <picture class="hero-image">
            <source srcset={banner[0]} type="image/webp">
            <source srcset={banner[1]} type="image/jpg">
            <img alt={$_('WebBattlePassTitle', { default: 'Season Pass' })} src={banner[0]}>
        </picture>
        <div class="hero-overlay"></div>
        <div class="hero-content">
            <h2>{$_('WebBattlePassTitle', { default: 'Season Pass' })}</h2>
            <div class="earn-rate">
                <span class="rate">{earnRateLabel}</span>
                <span class="rate-note">{$_('WebBattlePassEarnRateNote', { default: 'Earn points on every purchase — plus a free daily bonus.' })}</span>
            </div>
            <div class="timer-chip">
                {#if claimWindowOver}
                    <span class="season-state">{$_('WebBattlePassClaimWindowOver', { default: 'This season has ended.' })}</span>
                {:else if seasonOver}
                    <span class="season-state">{$_('WebBattlePassSeasonOver', { default: 'Season ended — claim your rewards!' })}</span>
                    <Countdown endDate={event.claimBy}>
                        {$_('WebBattlePassClaimBy', { default: 'Claim before' })}
                    </Countdown>
                {:else}
                    <Countdown endDate={event.finishAt}>
                        {$_('WebBattlePassSeasonEnds', { default: 'Season ends in' })}
                    </Countdown>
                {/if}
            </div>
        </div>
    </div>

    <div class="status-row">
        <div class="points">
            <span class="value">{$number(event.points)}</span>
            <span class="label">{$_('WebBattlePassPoints', { default: 'Points' })}</span>
        </div>

        <div class="actions">
            {#if !seasonOver}
                <button type="button" class="button primary daily-button"
                        class:orange={!dailyOnCooldown}
                        class:loading={claimingDaily}
                        disabled={dailyOnCooldown || claimingDaily}
                        onclick={handleDailyBonus}>
                    {#if dailyOnCooldown}
                        <Countdown endDate={nextDailyAt}>
                            {$_('WebBattlePassDailyClaimed', { default: 'Next bonus in' })}
                        </Countdown>
                    {:else}
                        {$_('WebBattlePassDailyBonus', { default: 'Daily Bonus' })} +{event.dailyBonus.points}
                    {/if}
                </button>
            {/if}

            {#if !event.hasEventPass && !claimWindowOver}
                <button type="button" class="button primary orange premium-button" onclick={() => onunlockpremium?.()}>
                    {$_('WebBattlePassUnlockPremium', { default: 'Unlock Premium' })} — {premiumPriceLabel(event, seasonPassPack)}
                </button>
            {/if}

            <button type="button" class="button primary claim-all-button"
                    class:green={hasAnyClaimable && !claimingAll}
                    disabled={!hasAnyClaimable || claimingAll}
                    onclick={handleClaimAll}>
                {#if claimingAll}
                    <span class="bp-spinner"></span>
                {:else}
                    {$_('WebBattlePassClaimAll', { default: 'Claim All' })}
                {/if}
            </button>
        </div>
    </div>
</div>

<style lang="scss">
  @use './battlepass.scss' as bp;

  .bp-header {
    margin: 0 0 14px;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #8a6228;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45);
    background: #240c05;

    .hero {
      position: relative;
      height: 180px;
      overflow: hidden;

      .hero-image {
        position: absolute;
        inset: 0;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          // Show the top of the scene; overscan slightly so the art's thin
          // white frame is pushed past the (overflow:hidden) hero edges.
          object-position: center top;
          transform: scale(1.06);
        }
      }

      .hero-overlay {
        position: absolute;
        inset: 0;
        background: linear-gradient(90deg, rgba(36, 12, 5, 0.88) 0%, rgba(36, 12, 5, 0.5) 45%, rgba(36, 12, 5, 0.12) 100%),
        linear-gradient(0deg, rgba(36, 12, 5, 0.8) 0%, transparent 40%);
      }

      .hero-content {
        position: relative;
        z-index: 1;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 0 28px;
        max-width: 640px;

        h2 {
          margin: 0;
          font-family: var(--font-family-sans-condensed);
          font-weight: bold;
          font-size: 2.6em;
          line-height: 1.1;
          color: rgb(254, 255, 219);
          text-shadow: 0 0 8px rgba(0, 0, 0, 0.6), 0 2px 0 rgba(0, 0, 0, 0.4);
        }

        .earn-rate {
          display: flex;
          flex-direction: column;
          margin: 8px 0 10px;

          .rate {
            display: inline-block;
            align-self: flex-start;
            padding: 4px 16px;
            border-radius: 8px;
            background: linear-gradient(180deg, #ffcc00 0%, #ff9d2e 100%);
            border: 1px solid #fff3c4;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.5);
            font-family: var(--font-family-sans-condensed);
            font-weight: bold;
            font-size: 1.5em;
            line-height: 1.1;
            color: #2a1004;
            text-shadow: 0 1px 0 rgba(255, 255, 255, 0.35);
          }

          .rate-note {
            margin-top: 4px;
            font-size: 0.9em;
            color: rgb(255, 224, 110);
            text-shadow: 0 1px 3px rgba(0, 0, 0, 0.95);
          }
        }

        .timer-chip {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          align-self: flex-start;
          padding: 4px 14px;
          border-radius: 14px;
          background: rgba(26, 9, 2, 0.85);
          border: 1px solid rgba(255, 227, 132, 0.55);
          font-weight: bold;
          font-size: 1.05em;
          color: rgb(255, 228, 133);

          .season-state {
            color: #ffcc00;
          }
        }
      }
    }

    .status-row {
      display: flex;
      align-items: center;
      gap: 14px;
      flex-wrap: wrap;
      padding: 12px 20px;
      background: linear-gradient(180deg, rgb(255, 232, 150) 0%, rgb(243, 205, 110) 100%);
      border-top: 2px solid #8a6228;

      .points {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        margin-right: auto;
        min-width: 96px;
        min-height: 76px;
        padding: 6px 14px;
        border-radius: 50%;
        background: radial-gradient(ellipse at 35% 30%, #5a2c0e 0%, #240c05 85%);
        border: 3px solid #d8a93c;
        box-shadow: 0 2px 8px rgba(90, 50, 10, 0.45), inset 0 1px 0 rgba(255, 227, 132, 0.25);

        .value {
          font-family: var(--font-family-sans-condensed);
          font-size: 1.8em;
          font-weight: bold;
          line-height: 1.1;
          color: #ffcc00;
          text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
        }

        .label {
          font-size: 0.72em;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: rgb(255, 227, 132);
        }
      }

      .actions {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;

        button {
          width: 220px;
          min-height: 42px;
          padding: 0.3rem 0.6rem;
          font-size: 16px;
          font-family: var(--font-family-sans-condensed);
          letter-spacing: 0.03em;
        }

        .daily-button:disabled,
        .claim-all-button:disabled {
          @include bp.bp-disabled-button;
        }

        .bp-spinner {
          @include bp.bp-button-spinner;
        }
      }
    }
  }

  @keyframes bp-spin {
    to { transform: rotate(360deg); }
  }

  @media (max-width: 700px) {
    .bp-header {
      .hero {
        // Let the hero grow with its content instead of clipping it at a fixed
        // height: the title, earn-rate note and the (potentially wrapped, or
        // longer-in-other-languages) "Season ended…" timer must all stay visible.
        height: auto;
        min-height: 150px;

        .hero-content {
          // Vertical padding so the title isn't jammed under the rounded header
          // edge and the timer chip has room to breathe / wrap.
          padding: 16px 14px;

          h2 {
            font-size: 2em;
          }

          .earn-rate .rate {
            font-size: 1.3em;
          }

          .earn-rate .rate-note {
            font-size: 0.8em;
          }

          .timer-chip {
            // Stack the "Season ended…" label and the countdown instead of letting
            // the combined width overflow the hero on narrow screens / long locales.
            flex-wrap: wrap;
            gap: 4px 10px;
          }
        }
      }

      .status-row {
        padding: 10px 12px;
        justify-content: center;

        .points {
          margin-right: 0;
        }

        .actions {
          width: 100%;
          gap: 8px;

          button {
            flex: 1 1 45%;
            width: auto;
            min-height: 46px;
            padding: 0.35rem 0.6rem;
            font-size: 16px;
          }
        }
      }
    }
  }
</style>
